import { ListChecks, Save, Search } from 'lucide-react'
import { useState } from 'react'

import { Button, SelectField, StatusMessage, TextField } from '@krp/ui-kit'

import { ActionError } from '../../../shared/ActionError'
import { TEXT_KIND_TITLES } from '../model/selectors'
import { TEXT_QUERY_KINDS, type TestingAccess, type TextCheckActions, type TextCheckVm, type TextQueryKind } from '../model/types'
import { StateBadge, TestingGate } from './TestingParts'

const KIND_OPTIONS = TEXT_QUERY_KINDS.map((kind) => ({
  value: kind,
  label: TEXT_KIND_TITLES[kind],
}))
const NONE = ''

/**
 * Проверка запроса (RP-FB34): та же лестница, что у номенклатуры. Видно, на какой
 * ступени закончился поиск, выдачу, место нужного товара и чем он совпал.
 */
export function TextCheckScreen({
  access,
  initial,
  products,
  vm,
  actions,
}: {
  access: TestingAccess
  /** Запрос и товар, с которыми экран открыт (проверка сохранённого запроса); поля берут их при первом показе. */
  initial: { query: string; productId: string | null } | null
  products: readonly { id: string; name: string }[]
  vm: TextCheckVm
  actions: TextCheckActions
}) {
  const [query, setQuery] = useState(initial?.query ?? '')
  const [productId, setProductId] = useState(initial?.productId ?? NONE)
  const [kind, setKind] = useState<TextQueryKind>('other')

  const expected = productId === NONE ? null : productId
  const { checked } = vm
  const saveRequest =
    checked !== null && checked.query.trim() !== '' && checked.expectedProductId !== null
      ? { text: checked.query.trim(), productId: checked.expectedProductId }
      : null

  return (
    <TestingGate access={access}>
      <section aria-labelledby="platform-text-check-title" className="platform-testing">
        <header className="platform-resources__toolbar">
          <h2 id="platform-text-check-title">Поиск по тексту · Проверка</h2>
          <Button
            variant="ghost"
            icon={<ListChecks size={16} aria-hidden="true" />}
            onClick={actions.openQueries}
            actionId="platform.testing-text-check.open-queries"
          >
            Запросы
          </Button>
        </header>

        <section aria-label="Запрос">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              actions.check(query, expected)
            }}
          >
            <div className="platform-testing__fields">
              <TextField label="Что ищем" type="search" placeholder="название, артикул или штрихкод" value={query} onChange={setQuery} />
              <SelectField
                label="Должен найтись"
                value={productId}
                options={[
                  { value: NONE, label: 'Не задан — только посмотреть выдачу' },
                  ...products.map((product) => ({
                    value: product.id,
                    label: product.name,
                  })),
                ]}
                onChange={setProductId}
                actionId="platform.testing-text-check.product"
              />
            </div>
            <div className="platform-testing__actions">
              <Button
                type="submit"
                variant="primary"
                icon={<Search size={16} aria-hidden="true" />}
                busy={vm.busy}
                actionId="platform.testing-text-check.check"
              >
                Проверить
              </Button>
            </div>
          </form>
        </section>

        <ActionError error={vm.error} />
        {vm.busy && <StatusMessage kind="loading" title="Ищем…" />}

        {vm.step !== null && (
          <section aria-label="Результат">
            <h3>Результат</h3>
            {vm.verdict !== null && (
              <p>
                <StateBadge badge={vm.verdict} />
              </p>
            )}
            <dl className="platform-testing__scores">
              <div>
                <dt>Ступень</dt>
                <dd>{vm.step}</dd>
              </div>
              <div>
                <dt>Время ответа</dt>
                <dd>{vm.latency}</dd>
              </div>
            </dl>
            {vm.hits.length === 0 ? (
              <p className="platform-testing__note">Выдача пуста</p>
            ) : (
              <ol className="platform-testing__hits" aria-label="Выдача">
                {vm.hits.map((hit) => (
                  <li key={`${hit.rank}`} value={hit.rank} data-expected={hit.expected || undefined}>
                    {hit.name}
                    {hit.sku === null ? null : <small> · {hit.sku}</small>}
                    {hit.matchedBy === '' ? null : <small> ({hit.matchedBy})</small>}
                    {hit.expected ? <strong> · нужный товар</strong> : null}
                  </li>
                ))}
              </ol>
            )}
            {saveRequest !== null && (
              <div className="platform-testing__row">
                <SelectField
                  label="Вид запроса"
                  value={kind}
                  options={KIND_OPTIONS}
                  onChange={(value) => setKind(TEXT_QUERY_KINDS.find((item) => item === value) ?? 'other')}
                  actionId="platform.testing-text-check.kind"
                />
                <Button
                  variant="ghost"
                  icon={<Save size={16} aria-hidden="true" />}
                  onClick={() =>
                    actions.save({
                      ...saveRequest,
                      kind,
                    })
                  }
                  actionId="platform.testing-text-check.save"
                >
                  Сохранить как запрос
                </Button>
              </div>
            )}
          </section>
        )}
      </section>
    </TestingGate>
  )
}
